import { Button, Card } from 'flowbite-react';
import { CheckCircle2Icon, FileTextIcon, WalletIcon } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { getCurrencyFormatter } from '../../../lib/format.utils';
import { pathWithParams } from '../../../lib/routing.utils';
import { AppHeader } from '../../components/appHeader/appHeader.component';
import { VoucherType } from '../../constants/voucher.constants';
import { RouteIndex } from '../../routeIndex';
import type { PaymentConfirmation } from '../../types/credit.types';

/**
 * Payment Success View
 *
 * Displays:
 * - Success message with checkmark icon
 * - Order confirmation details (transaction ID, credits, amount, date)
 * - Action buttons to navigate to wallet or documents
 * - Email confirmation notice
 *
 * TODO: Send confirmation email via backend API
 * TODO: Update user's wallet balance in backend
 * TODO: Create transaction record in database
 */
const PaymentSuccessView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const paymentConfirmation = location.state?.paymentConfirmation as PaymentConfirmation | undefined;

    const currencyFormatter = getCurrencyFormatter('CZK', 'cs-CZ');

    // Redirect to wallet if no payment confirmation data
    useEffect(() => {
        if (!paymentConfirmation) {
            navigate(pathWithParams(RouteIndex.wallet, {}), { replace: true });
        }
    }, [paymentConfirmation, navigate]);

    const handleGoToWallet = useCallback(() => {
        navigate(pathWithParams(RouteIndex.wallet, {}));
    }, [navigate]);

    const handleGoToDocuments = useCallback(() => {
        navigate(pathWithParams(RouteIndex.documentList, {}));
    }, [navigate]);

    if (!paymentConfirmation) {
        return null;
    }

    const voucherFormatValue = () => {
        if (!paymentConfirmation.appliedVoucher) {
            return;
        }

        if (paymentConfirmation.appliedVoucher.voucher.type === VoucherType.PERCENTAGE) {
            return `${paymentConfirmation.appliedVoucher.voucher.value}%`;
        }

        if (paymentConfirmation.appliedVoucher.voucher.type === VoucherType.FIXED_AMOUNT) {
            return `-${currencyFormatter.format(paymentConfirmation.appliedVoucher.voucher.value)}`;
        }
    };

    const formattedDate = new Intl.DateTimeFormat('cs-CZ', {
        dateStyle: 'long',
        timeStyle: 'short'
    }).format(paymentConfirmation.purchasedAt);

    return (
        <>
            <AppHeader
                pageTitle='Platba úspěšná'
            />

            <div className='p-4 pt-16 md:pt-20 md:pb-12 flex flex-col items-center justify-center min-h-[calc(100vh-4rem)]'>
                <div className='max-w-2xl w-full'>
                    <Card>
                        {/* Success Icon and Message */}
                        <div className='flex flex-col items-center text-center mb-8'>
                            <div className='mb-6'>
                                <CheckCircle2Icon 
                                    className='text-green-500' 
                                    size={80} 
                                    strokeWidth={1.5}
                                />
                            </div>
                            <h1 className='text-3xl font-bold text-[#0b111e] mb-2'>
                                Platba byla úspěšná!
                            </h1>
                            <p className='text-lg text-gray-600'>
                                Děkujeme za váš nákup
                            </p>
                        </div>

                        {/* Order Details */}
                        <div className='bg-gray-50 rounded-lg p-6 mb-6'>
                            <h2 className='text-xl font-semibold text-gray-900 mb-4'>
                                Detaily objednávky
                            </h2>

                            <div className='space-y-3'>
                                {/* Transaction ID */}
                                <div className='flex justify-between items-center py-2 border-b border-gray-200'>
                                    <span className='text-gray-600'>Číslo transakce:</span>
                                    <span className='font-mono text-sm font-semibold text-gray-900'>
                                        {paymentConfirmation.transactionId}
                                    </span>
                                </div>

                                {/* Credits Purchased */}
                                <div className='flex justify-between items-center py-2 border-b border-gray-200'>
                                    <span className='text-gray-600'>Zakoupené kredity:</span>
                                    <span className='text-xl font-bold text-blue-600'>
                                        {paymentConfirmation.creditsPurchased}
                                    </span>
                                </div>
                                
                                {/* Applied Voucher */}
                                {paymentConfirmation.appliedVoucher && (
                                    <div className='flex justify-between items-center py-2 border-b border-gray-200'>
                                        <span className='text-gray-600'>Sleva - slevový poukaz:</span>
                                        <span className='text-sm font-bold text-green-600'>
                                            {voucherFormatValue()}
                                        </span>
                                    </div>
                                )}

                                {/* Amount Paid */}
                                <div className='flex justify-between items-center py-2 border-b border-gray-200'>
                                    <span className='text-gray-600'>Zaplacená částka:</span>
                                    <span className='text-xl font-bold text-gray-900'>
                                        {currencyFormatter.format(paymentConfirmation.amountPaid)}
                                    </span>
                                </div>

                                {/* Date and Time */}
                                <div className='flex justify-between items-center py-2'>
                                    <span className='text-gray-600'>Datum a čas:</span>
                                    <span className='font-medium text-gray-900'>
                                        {formattedDate}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Purchased Packages */}
                        <div className='mb-6'>
                            <h3 className='text-lg font-semibold text-gray-900 mb-3'>
                                Zakoupené balíčky
                            </h3>
                            <div className='space-y-2'>
                                {paymentConfirmation.packages.map(({ package: pkg, quantity }) => (
                                    <div 
                                        key={pkg.id} 
                                        className='flex justify-between items-center p-3 bg-white border border-gray-200 rounded-lg'
                                    >
                                        <div>
                                            <p className='font-medium text-gray-900'>{pkg.label}</p>
                                            <p className='text-sm text-gray-500'>
                                                {pkg.credits} kreditů × {quantity} = {pkg.credits * quantity} kreditů
                                            </p>
                                        </div>
                                        <div className='text-right'>
                                            <p className='font-semibold text-gray-900'>
                                                {currencyFormatter.format(pkg.price * quantity)}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Email Confirmation Notice */}
                        <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
                            <p className='text-sm text-blue-800'>
                                📧 Potvrzení objednávky bylo odesláno na váš e-mail{' '}
                                <span className='font-semibold'>{paymentConfirmation.billingInfo.email}</span>
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className='flex flex-col sm:flex-row gap-3'>
                            <Button
                                color="primary"
                                size="lg"
                                onClick={handleGoToWallet}
                                className="flex-1"
                            >
                                <WalletIcon className="mr-2 h-5 w-5" />
                                Zpět do peněženky
                            </Button>
                            <Button
                                color="light"
                                size="lg"
                                onClick={handleGoToDocuments}
                                className="flex-1"
                            >
                                <FileTextIcon className="mr-2 h-5 w-5" />
                                Pokračovat k dokumentům
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>
        </>
    );
};

export default PaymentSuccessView;

